import React, { Fragment, useState } from 'react';
import { IReaderStationStatus, Severity } from '../../../models/IReaderStatus';
import { useTranslation } from 'react-i18next';
import { Alert, AlertColor, Avatar, Card, CardActions, CardContent, CardHeader, Collapse, IconButton, IconButtonProps, styled, Typography } from '@mui/material';
import { format } from 'util';
import SpeedIcon from '@mui/icons-material/Speed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

interface IReaderMonitorDetailProps {
    reader: IReaderStationStatus;
}

export default function ReaderMonitorDetail(props: IReaderMonitorDetailProps) {
    const [expanded, setExpanded] = useState(false);
    const { reader } = props;
    const { t } = useTranslation("common");

    const getSeverityTranslation = (severity: Severity): string => {

        switch (severity) {
            case Severity.healthy:
                return t("monitoring.healthy");
            case Severity.warning:
                return t("monitoring.warning");
            case Severity.unhealthy:
                return t("monitoring.unhealthy");
        }
    }

    const convertSeverity = (severity: Severity): AlertColor => {
        switch (severity) {
            case Severity.healthy:
                return "success";
            case Severity.warning:
                return "warning";
            case Severity.unhealthy:
                return "error";
            default:
                return "warning";
        }
    }

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    return (

        <Card>
            <CardHeader
                avatar={<Avatar alt={reader.organizer?.name} src={reader.organizer?.portraitBlobAddressUri} />}
                title={reader.readerId}
                subheader={getSeverityTranslation(reader.severity)}
            />
            <CardContent>
                <Typography variant="body2" color="text.secondary">
                    {format(t("monitoring.readerSummaryTemplate"), reader.lastSeen, reader.lastCheck, reader.issues.length)}
                </Typography>
            </CardContent>
            <CardActions>
                <Link to={`/event/${reader.trackDay?.id}`}>
                    <IconButton aria-label={t<string>("mainList.events")}>
                        <SpeedIcon />
                    </IconButton>
                </Link>
                <ExpandMore
                    expand={expanded}
                    onClick={handleExpandClick}
                    aria-expanded={expanded}
                    aria-label={t<string>("commonLabels.showMore")}
                >
                    <ExpandMoreIcon />
                </ExpandMore>
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent >
                    {reader.issues.length === 0 ? <Typography variant="body2" color="text.secondary">{t("monitoring.healthy")}</Typography> :
                        reader.issues.map((issue, index) =>
                            <Fragment key={index}>
                                <Alert severity={convertSeverity(issue.severity)}>{issue.description}</Alert>
                                <Typography >
                                    {t("monitoring.issueResolution")}
                                </Typography>
                                <Typography paragraph>
                                    {issue.resolution}
                                </Typography>
                            </Fragment>)}
                </CardContent>
            </Collapse>
        </Card>);
}


